import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Community from "@/pages/Community";
import PostDetail from "@/pages/PostDetail";
import CreatePost from "@/pages/CreatePost";
import Messages from "@/pages/Messages";
import Conversation from "@/pages/Conversation";
import Garage from "@/pages/Garage";
import EditVehicle from "@/pages/EditVehicle";
import UserProfile from "@/pages/UserProfile";
import Admin from "@/pages/Admin";
import ControlPanel from "@/pages/ControlPanel";
import PlayGarage from "@/pages/PlayGarage";
import Favorites from "@/pages/Favorites";
import Onboarding from "@/pages/Onboarding";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import PartDetail from "@/pages/PartDetail";
import CreatePart from "@/pages/CreatePart";
import Events from "@/pages/Events";
import VehicleDetail from "@/pages/VehicleDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import VinnyBrain from "@/pages/VinnyBrain";
import SeoPage from "@/pages/SeoPage";
import Notifications from "@/pages/Notifications";

function AppRouter() {
    const location = useLocation();
    // Emergent Google callback returns to /#session_id=...
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/parts/new" element={<ProtectedRoute><CreatePart /></ProtectedRoute>} />
            <Route path="/parts/:part_id" element={<PartDetail />} />
            <Route path="/events" element={<Events />} />
            <Route path="/the-ladder" element={<Blog />} />
            <Route path="/the-ladder/:slug" element={<BlogPost />} />
            <Route
                path="/admin/vinny"
                element={
                    <ProtectedRoute adminOnly>
                        <VinnyBrain />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/play-garage"
                element={
                    <ProtectedRoute adminOnly>
                        <PlayGarage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/control"
                element={
                    <ProtectedRoute adminOnly>
                        <ControlPanel />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/control/:section"
                element={
                    <ProtectedRoute adminOnly>
                        <ControlPanel />
                    </ProtectedRoute>
                }
            />
            {/* Programmatic SEO/GEO pages — universal renderer */}
            <Route path="/research/:platform/:trim/:year" element={<SeoPage />} />
            <Route path="/find/:trim/:loc" element={<SeoPage />} />
            <Route path="/compare/:pair" element={<SeoPage />} />
            <Route path="/buy/:trim/:year" element={<SeoPage />} />
            <Route path="/trim/:slug" element={<SeoPage />} />
            <Route path="/platform/:slug" element={<SeoPage />} />

            <Route element={<Layout />}>
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/listings/:id" element={<ListingDetail />} />
                <Route
                    path="/listings/new"
                    element={
                        <ProtectedRoute>
                            <CreateListing />
                        </ProtectedRoute>
                    }
                />
                <Route path="/community" element={<Community />} />
                <Route path="/community/:id" element={<PostDetail />} />
                <Route
                    path="/community/new"
                    element={
                        <ProtectedRoute>
                            <CreatePost />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/messages"
                    element={
                        <ProtectedRoute>
                            <Messages />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/messages/:id"
                    element={
                        <ProtectedRoute>
                            <Conversation />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/garage"
                    element={
                        <ProtectedRoute>
                            <Garage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/garage/vehicles/:id"
                    element={
                        <ProtectedRoute>
                            <EditVehicle />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/garage/vehicles/new"
                    element={
                        <ProtectedRoute>
                            <EditVehicle />
                        </ProtectedRoute>
                    }
                />
                <Route path="/vehicles/:id" element={<VehicleDetail />} />
                <Route path="/users/:id" element={<UserProfile />} />
                <Route
                    path="/favorites"
                    element={
                        <ProtectedRoute>
                            <Favorites />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/notifications"
                    element={
                        <ProtectedRoute>
                            <Notifications />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute adminOnly>
                            <Admin />
                        </ProtectedRoute>
                    }
                />
            </Route>
        </Routes>
    );
}

function App() {
    return (
        <div className="App dark">
            <HelmetProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <AppRouter />
                        <Toaster position="top-center" richColors />
                    </BrowserRouter>
                </AuthProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
